// services/locationTrackingService.js
import io from "socket.io-client";

class LocationTrackingService {
    constructor() {
        this.isTracking = false;
        this.intervalId = null;
        this.watchId = null;
        this.db = null;
        this.socket = null;
        this.wakeLock = null;
        this.TRACKING_INTERVAL = 120000; // 2 minutes
        this.connectionAttempts = 0;
        this.maxConnectionAttempts = 3;
        this._swMessageHandler = null;
        this._wakeLockReleaseHandler = null;
    }

    async init() {
        try {
            console.log("🚀 Initializing Location Tracking Service...");

            // Open IndexedDB
            this.db = await this.openDB();
            console.log("✅ IndexedDB initialized");

            // Initialize Socket.IO FIRST
            await this.initSocket();
            console.log("✅ Socket initialized");

            // Register service worker
            await this.registerServiceWorker();
            console.log("✅ Service Worker registered");

            // Set up service worker message handler (respond to REQUEST_LOCATION)
            this.handleServiceWorkerMessage();
            console.log("✅ Service Worker message handler set up");

            // Request permissions
            await this.requestPermissions();
            console.log("✅ Permissions requested");

            console.log("✅ Location tracking service fully initialized");
        } catch (error) {
            console.error("❌ Error initializing location tracking service:", error);
            throw error;
        }
    }

    async openDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open("LocationDB", 1);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains("locations")) {
                    db.createObjectStore("locations", { keyPath: "id", autoIncrement: true });
                }
            };
        });
    }

    async initSocket() {
        return new Promise((resolve, reject) => {
            try {
                // Get socket URL from environment or use default
                const socketURL = process.env.REACT_APP_SOCKET_URL || "https://zazio-89059772666.us-central1.run.app/";
                const token = localStorage.getItem("token");

                console.log("🔌 Connecting to Socket.IO...");
                console.log("Socket URL:", socketURL);
                console.log("Token present:", !!token);

                // Initialize socket connection
                this.socket = io(socketURL, {
                    auth: {
                        token: token,
                    },
                    transports: ["websocket", "polling"],
                    reconnection: true,
                    reconnectionDelay: 1000,
                    reconnectionAttempts: 5,
                    timeout: 10000,
                });

                // Socket event listeners
                this.socket.on("connect", () => {
                    console.log("✅ Socket CONNECTED successfully!");
                    console.log("Socket ID:", this.socket.id);
                    console.log("Transport:", this.socket.io.engine.transport.name);
                    this.connectionAttempts = 0;
                    resolve();
                });

                this.socket.on("disconnect", (reason) => {
                    console.warn("⚠️ Socket disconnected:", reason);
                    if (reason === "io server disconnect") {
                        // Server disconnected, try to reconnect
                        this.socket.connect();
                    }
                });

                this.socket.on("connect_error", (error) => {
                    console.error("❌ Socket connection error:", error.message);
                    this.connectionAttempts++;

                    if (this.connectionAttempts >= this.maxConnectionAttempts) {
                        console.error("❌ Max connection attempts reached");
                        reject(new Error("Failed to connect to socket server"));
                    }
                });

                this.socket.on("location-saved", (data) => {
                    console.log("✅ Location saved confirmation:", data);
                });

                this.socket.on("location-error", (error) => {
                    console.error("❌ Location error from server:", error);
                });

                this.socket.on("reconnect", (attemptNumber) => {
                    console.log("🔄 Socket reconnected after", attemptNumber, "attempts");
                });

                this.socket.on("reconnect_attempt", (attemptNumber) => {
                    console.log("🔄 Attempting to reconnect...", attemptNumber);
                });

                this.socket.on("reconnect_error", (error) => {
                    console.error("❌ Reconnection error:", error.message);
                });

                this.socket.on("reconnect_failed", () => {
                    console.error("❌ Reconnection failed");
                });

                // Timeout if connection takes too long
                setTimeout(() => {
                    if (!this.socket.connected) {
                        console.warn("⚠️ Socket connection timeout, but continuing...");
                        resolve(); // Don't reject, allow offline mode
                    }
                }, 10000);
            } catch (error) {
                console.error("❌ Error initializing socket:", error);
                reject(error);
            }
        });
    }

    async registerServiceWorker() {
        if ("serviceWorker" in navigator) {
            try {
                const registration = await navigator.serviceWorker.register("/service-worker.js");
                console.log("Service Worker registered:", registration.scope);
            } catch (error) {
                console.log("Service Worker registration failed (not critical):", error.message);
            }
        }
    }

    // Listen for REQUEST_LOCATION messages from the service worker and respond with current position
    handleServiceWorkerMessage() {
        if (!("serviceWorker" in navigator)) return;

        // Remove previous handler to avoid duplicates
        if (this._swMessageHandler) {
            navigator.serviceWorker.removeEventListener("message", this._swMessageHandler);
        }

        this._swMessageHandler = (event) => {
            const { type } = event.data || {};

            if (type === "REQUEST_LOCATION") {
                // Service worker wants current location - get it and reply via port
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        if (event.ports && event.ports[0]) {
                            event.ports[0].postMessage({
                                lat: position.coords.latitude,
                                lng: position.coords.longitude,
                                accuracy: position.coords.accuracy,
                            });
                        }
                    },
                    (error) => {
                        console.warn("❌ Geolocation error responding to SW:", error.message);
                        if (event.ports && event.ports[0]) {
                            event.ports[0].postMessage({ error: error.message });
                        }
                    },
                    { enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 },
                );
            } else if (type === "GET_TOKEN") {
                if (event.ports && event.ports[0]) {
                    event.ports[0].postMessage({ token: localStorage.getItem("token") });
                }
            }
        };

        navigator.serviceWorker.addEventListener("message", this._swMessageHandler);
        console.log("✅ SW message handler registered");
    }

    // Request Wake Lock to keep the screen/CPU awake on Android
    async requestWakeLock() {
        if (!("wakeLock" in navigator)) {
            console.log("ℹ️ Wake Lock API not supported on this device");
            return;
        }

        try {
            // Remove old event listener if it exists
            if (this.wakeLock && this._wakeLockReleaseHandler) {
                try {
                    this.wakeLock.removeEventListener("release", this._wakeLockReleaseHandler);
                } catch (e) {
                    // Ignore errors when removing listener from released lock
                }
            }

            // If wake lock exists and is released, clear the reference
            if (this.wakeLock?.released) {
                console.log("⚠️ Previous wake lock was released, clearing reference");
                this.wakeLock = null;
            }

            // Request a new wake lock
            this.wakeLock = await navigator.wakeLock.request("screen");
            console.log("✅ Wake Lock acquired");

            // Set up release handler to re-acquire
            this._wakeLockReleaseHandler = async () => {
                console.log("⚠️ Wake Lock released, will re-acquire if still tracking...");

                // Clear the reference to the released lock
                this.wakeLock = null;

                // Re-acquire if still tracking
                if (this.isTracking && document.visibilityState === "visible") {
                    try {
                        await this.requestWakeLock();
                    } catch (err) {
                        console.warn("⚠️ Failed to re-acquire wake lock:", err.message);
                    }
                }
            };

            // Add the release event listener
            this.wakeLock.addEventListener("release", this._wakeLockReleaseHandler);

            // Also handle visibility changes to re-acquire wake lock when page becomes visible
            if (!this._visibilityChangeHandler) {
                this._visibilityChangeHandler = async () => {
                    if (this.isTracking && document.visibilityState === "visible") {
                        // Wait a bit for the page to fully become visible
                        setTimeout(async () => {
                            if (!this.wakeLock || this.wakeLock.released) {
                                console.log("🔄 Page visible again, re-acquiring wake lock...");
                                try {
                                    await this.requestWakeLock();
                                } catch (err) {
                                    console.warn("⚠️ Failed to re-acquire wake lock on visibility change:", err.message);
                                }
                            }
                        }, 100);
                    }
                };
                document.addEventListener("visibilitychange", this._visibilityChangeHandler);
            }
        } catch (error) {
            console.warn("⚠️ Wake Lock request failed:", error.message);

            // If it's because the document is not visible, that's expected
            if (error.name === "NotAllowedError" && document.visibilityState !== "visible") {
                console.log("ℹ️ Cannot acquire wake lock when page is not visible (this is normal)");
            }
        }
    }

    // Release Wake Lock
    releaseWakeLock() {
        if (this.wakeLock && !this.wakeLock.released) {
            try {
                // Remove event listener before releasing
                if (this._wakeLockReleaseHandler) {
                    this.wakeLock.removeEventListener("release", this._wakeLockReleaseHandler);
                }

                this.wakeLock.release();
                console.log("✅ Wake Lock released");
            } catch (error) {
                console.warn("⚠️ Error releasing wake lock:", error.message);
            }
        }

        this.wakeLock = null;
        this._wakeLockReleaseHandler = null;

        // Remove visibility change handler
        if (this._visibilityChangeHandler) {
            document.removeEventListener("visibilitychange", this._visibilityChangeHandler);
            this._visibilityChangeHandler = null;
        }
    }

    async requestPermissions() {
        // Request location permission
        if ("geolocation" in navigator) {
            try {
                await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject);
                });
                console.log("✅ Location permission granted");
            } catch (error) {
                console.error("❌ Location permission denied:", error);
            }
        }

        // Request notification permission
        if ("Notification" in window) {
            if (Notification.permission === "default") {
                const permission = await Notification.requestPermission();
                console.log("Notification permission:", permission);
            }
        }
    }

    async startTracking(deliveryId, driverId) {
        if (this.isTracking) {
            console.log("⚠️ Tracking already active");
            return;
        }

        const userId = localStorage.getItem("userId");

        console.log("🎯 Starting location tracking...");
        console.log("Delivery ID:", deliveryId);
        console.log("Driver ID:", driverId);
        console.log("User ID:", userId);

        // Only track if logged-in user is the driver
        if (userId !== driverId) {
            console.error("❌ User is not the assigned driver");
            console.log("Expected Driver ID:", driverId);
            console.log("Actual User ID:", userId);
            return;
        }

        // Check socket connection
        if (!this.socket) {
            console.error("❌ Socket not initialized!");
            return;
        }

        if (!this.socket.connected) {
            console.warn("⚠️ Socket not connected, attempting to connect...");
            this.socket.connect();

            // Wait for connection
            await new Promise((resolve) => {
                const timeout = setTimeout(() => {
                    console.warn("⚠️ Socket connection timeout");
                    resolve();
                }, 5000);

                this.socket.once("connect", () => {
                    clearTimeout(timeout);
                    console.log("✅ Socket connected for tracking");
                    resolve();
                });
            });
        }

        this.isTracking = true;
        console.log("✅ Location tracking ACTIVE");

        // Save tracking state
        localStorage.setItem("activeDeliveryId", deliveryId);
        localStorage.setItem("activeDriverId", driverId);
        localStorage.setItem("isTrackingLocation", "true");

        // Acquire Wake Lock to keep device awake
        await this.requestWakeLock();

        // Notify service worker to start background GPS tracking
        await this.notifyServiceWorker("START_TRACKING", deliveryId, driverId);

        // Start immediate tracking via socket too
        await this.trackLocation(deliveryId, driverId);

        // Set up interval tracking (every 2 minutes)
        this.intervalId = setInterval(async () => {
            console.log("⏰ Interval tracking triggered...");
            await this.trackLocation(deliveryId, driverId);
        }, this.TRACKING_INTERVAL);

        console.log(`✅ Tracking interval set to ${this.TRACKING_INTERVAL / 1000} seconds`);
    }

    async trackLocation(deliveryId, driverId) {
        try {
            console.log("📍 Getting current position...");
            const position = await this.getCurrentPosition();

            const locationData = {
                deliveryId,
                driverId,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: new Date().toISOString(),
                userId: localStorage.getItem("userId"),
            };

            console.log("📍 Location acquired:", {
                lat: locationData.latitude.toFixed(6),
                lng: locationData.longitude.toFixed(6),
                accuracy: locationData.accuracy.toFixed(2) + "m",
                time: new Date(locationData.timestamp).toLocaleTimeString(),
            });

            // Check socket status
            console.log("🔌 Socket status:", {
                exists: !!this.socket,
                connected: this.socket?.connected,
                id: this.socket?.id,
            });

            // Try to emit via socket
            if (this.socket && this.socket.connected) {
                console.log("📤 Emitting location via socket...");
                this.socket.emit("driver-location", locationData);
                console.log("✅ Location emitted successfully!");
            } else {
                console.warn("⚠️ Socket not connected, storing offline");
                await this.storeLocationOffline(locationData);

                // Try to reconnect
                if (this.socket) {
                    console.log("🔄 Attempting to reconnect socket...");
                    this.socket.connect();
                }
            }
        } catch (error) {
            console.error("❌ Error tracking location:", error);
        }
    }

    getCurrentPosition() {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 5000,
            });
        });
    }

    async storeLocationOffline(locationData) {
        if (!this.db) {
            console.error("❌ IndexedDB not initialized");
            return;
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(["locations"], "readwrite");
            const store = transaction.objectStore("locations");
            const request = store.add(locationData);

            request.onsuccess = () => {
                console.log("💾 Location stored offline");
                resolve();
            };
            request.onerror = () => {
                console.error("❌ Error storing location offline:", request.error);
                reject(request.error);
            };
        });
    }

    stopTracking() {
        console.log("🛑 Stopping location tracking...");

        this.isTracking = false;

        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
            console.log("✅ Tracking interval cleared");
        }

        if (this.watchId) {
            navigator.geolocation.clearWatch(this.watchId);
            this.watchId = null;
            console.log("✅ Geolocation watch cleared");
        }

        // Notify service worker to stop background tracking
        this.notifyServiceWorker("STOP_TRACKING").catch(() => {});

        // Release Wake Lock
        this.releaseWakeLock();

        // Clear tracking state
        localStorage.removeItem("activeDeliveryId");
        localStorage.removeItem("activeDriverId");
        localStorage.removeItem("isTrackingLocation");

        console.log("✅ Location tracking stopped");
    }

    // Send a message to the active service worker
    async notifyServiceWorker(type, deliveryId, driverId) {
        if (!("serviceWorker" in navigator)) return;
        try {
            const registration = await navigator.serviceWorker.ready;
            if (registration && registration.active) {
                registration.active.postMessage({
                    type,
                    deliveryId,
                    driverId,
                    token: localStorage.getItem("token"),
                });
                console.log(`✅ Sent ${type} to Service Worker`);
            }
        } catch (error) {
            console.warn(`⚠️ Could not send ${type} to Service Worker:`, error.message);
        }
    }

    async resumeTrackingIfNeeded() {
        const isTracking = localStorage.getItem("isTrackingLocation");
        const deliveryId = localStorage.getItem("activeDeliveryId");
        const driverId = localStorage.getItem("activeDriverId");

        console.log("🔍 Checking for active tracking to resume...");
        console.log("Is tracking:", isTracking);
        console.log("Delivery ID:", deliveryId);
        console.log("Driver ID:", driverId);

        if (isTracking === "true" && deliveryId && driverId) {
            console.log("🔄 Resuming location tracking...");
            await this.startTracking(deliveryId, driverId);
        } else {
            console.log("ℹ️ No active tracking to resume");
        }
    }

    // Manual test method
    async testConnection() {
        console.log("🧪 Testing socket connection...");

        if (!this.socket) {
            console.error("❌ Socket not initialized");
            return false;
        }

        console.log("Socket state:", {
            connected: this.socket.connected,
            id: this.socket.id,
            transport: this.socket.io?.engine?.transport?.name,
        });

        if (this.socket.connected) {
            console.log("✅ Socket is connected");

            // Try emitting a test location
            const testLocation = {
                deliveryId: "test",
                driverId: "test",
                latitude: 33.006257,
                longitude: 73.15074,
                accuracy: 10,
                timestamp: new Date().toISOString(),
                userId: localStorage.getItem("userId"),
            };

            console.log("📤 Emitting test location...");
            this.socket.emit("driver-location", testLocation);
            return true;
        } else {
            console.error("❌ Socket is NOT connected");
            return false;
        }
    }
}

// Export singleton instance
const locationTrackingService = new LocationTrackingService();

// Make it available globally for debugging
if (typeof window !== "undefined") {
    window.locationTrackingService = locationTrackingService;
}

export default locationTrackingService;
